import React from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Shown when the server returns 409 requires_confirmation: admin may override.
export const ConfirmWarningsDialog = ({ open, items, busy, onCancel, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
    <AlertDialogContent className="max-w-lg" data-testid="confirm-warnings-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
          <AlertTriangle className="w-5 h-5" /> Please confirm
        </AlertDialogTitle>
        <AlertDialogDescription>
          {items?.length === 1 ? 'This assignment has a warning.' : `${items?.length || 0} assignments have warnings.`} You can still assign.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <ul className="max-h-72 overflow-y-auto space-y-2" data-testid="confirm-warnings-list">
        {(items || []).map((it, i) => (
          <li key={i} className="rounded-lg border border-orange-200 bg-orange-50/60 p-3">
            <div className="text-sm font-semibold text-slate-800">{it.official}</div>
            <div className="text-xs text-slate-500 mb-1">{it.game}</div>
            {it.warnings.map((w, j) => (
              <div key={j} className="text-xs text-orange-800 flex gap-1.5"><span>•</span>{w.message}</div>
            ))}
          </li>
        ))}
      </ul>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="confirm-warnings-cancel">Go back</AlertDialogCancel>
        <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={busy}
          className="bg-orange-600 hover:bg-orange-700" data-testid="confirm-warnings-override">
          Assign anyway
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Approved/paid fees exist for these officials: admin must acknowledge before the change goes through.
export const FinanceAckDialog = ({ open, detail, busy, onCancel, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
    <AlertDialogContent className="max-w-lg" data-testid="finance-ack-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" /> Payment records affected</AlertDialogTitle>
        <AlertDialogDescription>{detail?.message}</AlertDialogDescription>
      </AlertDialogHeader>
      <ul className="space-y-1.5" data-testid="finance-ack-list">
        {(detail?.items || []).map((it, i) => (
          <li key={i} className="flex justify-between gap-3 rounded-lg border border-red-200 bg-red-50/60 px-3 py-2 text-sm">
            <span className="font-semibold text-slate-800">{it.official} <span className="font-normal text-slate-500">· {it.role}</span></span>
            <span className="text-xs text-slate-600 whitespace-nowrap">Fee {it.amount} · Paid {it.paid}</span>
          </li>
        ))}
      </ul>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="finance-ack-cancel">Go back</AlertDialogCancel>
        <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="finance-ack-confirm">
          I understand, continue
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Runs a request as send(override, ackFinance); on 409 asks the admin to confirm, then retries.
export function useOverrideFlow() {
  const [pending, setPending] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const run = (send) => {
    const ask = (kind, detail, retry) => new Promise((resolve, reject) => setPending({ kind, detail, retry, resolve, reject }));
    const attempt = async (override, ack) => {
      try {
        return await send(override, ack);
      } catch (e) {
        if (e.status === 409 && e.detail?.requires_finance_ack && !ack) return ask('finance', e.detail, () => attempt(override, true));
        if (e.status === 409 && e.detail?.requires_confirmation && !override) return ask('warn', e.detail, () => attempt(true, ack));
        throw e;
      }
    };
    return attempt(false, false);
  };
  const confirm = async () => {
    const p = pending;
    setPending(null);
    setBusy(true);
    try { p.resolve(await p.retry()); } catch (e) { p.reject(e); } finally { setBusy(false); }
  };
  const cancel = () => { pending?.resolve(null); setPending(null); };
  const dialog = (
    <>
      <ConfirmWarningsDialog open={pending?.kind === 'warn'} items={pending?.detail?.items} busy={busy} onCancel={cancel} onConfirm={confirm} />
      <FinanceAckDialog open={pending?.kind === 'finance'} detail={pending?.detail} busy={busy} onCancel={cancel} onConfirm={confirm} />
    </>
  );
  return { run, dialog };
}
